import type { CrawlRun } from "@/services/api/job-postings";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

interface CrawlRunsTableProps {
  runs: CrawlRun[];
  isLoading: boolean;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDuration(startedAt: string, completedAt?: string | null) {
  if (!completedAt) return "—";
  const ms =
    new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function StatusBadge({ status }: { status: string }) {
  switch (status.toLowerCase()) {
    case "completed":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    case "running":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
          <Loader2 className="h-3 w-3 animate-spin" />
          Running
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-grid-100 px-2 py-0.5 text-xs font-medium text-ink-600">
          {status}
        </span>
      );
  }
}

export function CrawlRunsTable({ runs, isLoading }: CrawlRunsTableProps) {
  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">
        Loading crawl runs...
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">
        No crawl runs recorded yet
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-grid-200 bg-grid-50/50">
            <th className="px-4 py-2.5 text-left font-medium text-ink-600">
              Source
            </th>
            <th className="px-4 py-2.5 text-left font-medium text-ink-600">
              Status
            </th>
            <th className="px-4 py-2.5 text-left font-medium text-ink-600">
              Started
            </th>
            <th className="px-4 py-2.5 text-left font-medium text-ink-600">
              Duration
            </th>
            <th className="px-4 py-2.5 text-right font-medium text-ink-600">
              New
            </th>
            <th className="px-4 py-2.5 text-right font-medium text-ink-600">
              Updated
            </th>
            <th className="px-4 py-2.5 text-right font-medium text-ink-600">
              Deactivated
            </th>
            <th className="px-4 py-2.5 text-left font-medium text-ink-600">
              Error
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-grid-100">
          {runs.map((run) => (
            <tr key={run.id} className="hover:bg-grid-50/50 transition-colors">
              <td className="px-4 py-2.5 font-medium text-ink-900">
                <div>{run.sourceName}</div>
                {run.triggeredBy && (
                  <div className="text-xs font-normal text-ink-400">
                    by {run.triggeredBy}
                  </div>
                )}
              </td>
              <td className="px-4 py-2.5">
                <StatusBadge status={run.status} />
              </td>
              <td className="px-4 py-2.5 text-ink-600">
                {formatDate(run.startedAt)}
              </td>
              <td className="px-4 py-2.5 text-ink-600">
                {formatDuration(run.startedAt, run.completedAt)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink-900">
                {run.newPostings > 0 ? (
                  <span className="text-emerald-600">+{run.newPostings}</span>
                ) : (
                  run.newPostings
                )}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink-900">
                {run.updatedPostings > 0 ? (
                  <span className="text-blue-600">{run.updatedPostings}</span>
                ) : (
                  run.updatedPostings
                )}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink-900">
                {run.deactivatedPostings > 0 ? (
                  <span className="text-amber-600">
                    {run.deactivatedPostings}
                  </span>
                ) : (
                  run.deactivatedPostings
                )}
              </td>
              <td className="px-4 py-2.5 text-ink-500 max-w-[200px] truncate">
                {run.errorMessage || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
